import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import axios from 'axios';
import API_BASE from '../../../config/api';

export type Plan = 'starter' | 'growth' | 'professional';

interface Props {
  selected: Plan;
  onSelect: (plan: Plan) => void;
  onNext: () => void;
}

export default function StepPlan({ selected, onSelect, onNext }: Props) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<any[]>([]);

  useEffect(() => {
    axios.get(`${API_BASE}/plans`).then((r) => setPlans(r.data)).catch(console.error);
  }, []);

  const selectedPlan = plans.find((p) => p.plan_key === selected);
  const trialDays = plans[0]?.trial_days ?? 14;

  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        onClick={() => navigate(-1)}
        sx={{ position: 'absolute', top: 16, right: 16, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <CloseIcon />
      </IconButton>
      <Typography variant="h5" fontWeight={700} textAlign="center" mb={1}>
        Choose your Shield plan
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={4}>
        {trialDays}-day free trial on all plans — no credit card required
      </Typography>

      {plans.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 4,
          }}
        >
          {plans.map((plan) => {
            const isSelected = selected === plan.plan_key;
            return (
              <Card
                key={plan.plan_key}
                onClick={() => onSelect(plan.plan_key as Plan)}
                sx={{
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'rgba(255,255,255,0.08)',
                  borderRadius: 3,
                  transition: 'all 0.2s',
                  bgcolor: isSelected ? 'rgba(0,180,216,0.06)' : 'background.paper',
                  position: 'relative',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(0,180,216,0.04)' },
                }}
              >
                {plan.is_popular && (
                  <Box sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }}>
                    <Chip
                      icon={<StarIcon sx={{ fontSize: 14 }} />}
                      label="Most Popular"
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 700, fontSize: 11 }}
                    />
                  </Box>
                )}
                <CardContent sx={{ pt: plan.is_popular ? 3.5 : 2.5 }}>
                  <Typography variant="h6" fontWeight={700} mb={0.5}>{plan.display_name}</Typography>
                  <Typography variant="h5" fontWeight={800} color="primary.main" mb={2}>
                    ${plan.price_monthly}<Typography component="span" variant="body2" color="text.secondary">/mo</Typography>
                  </Typography>
                  <Stack spacing={0.75}>
                    {(plan.features as string[]).map((f) => (
                      <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
                        <Typography variant="body2">{f}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Button
                    fullWidth
                    variant={isSelected ? 'contained' : 'outlined'}
                    sx={{ mt: 3, fontWeight: 700 }}
                    onClick={(e) => { e.stopPropagation(); onSelect(plan.plan_key as Plan); }}
                  >
                    {isSelected ? 'Selected' : 'Start Free Trial'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Box textAlign="center">
        <Button
          variant="contained"
          size="large"
          onClick={onNext}
          disabled={plans.length === 0}
          sx={{ px: 6, fontWeight: 700 }}
        >
          Continue with {selectedPlan?.display_name ?? '…'} →
        </Button>
        <Typography variant="body2" color="text.secondary" mt={3}>
          Already have an account?{' '}
          <Link component={RouterLink} to="/login" color="primary" fontWeight={600}>
            Sign in →
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}
